import type { GetBrandDTO } from "@nlc/shared-types";
import type { ReactNode } from "react";

import { ExternalLinkIcon, LinkIcon } from "lucide-react";

import { SectionHeader } from "../SectionHeader";

type OverviewTabProps = {
  brand: Pick<GetBrandDTO, "basicInformation" | "name" | "socialLinks">;
};

export function OverviewTab({ brand }: OverviewTabProps) {
  const { basicInformation, name, socialLinks } = brand;

  return (
    <div className="flex flex-col gap-xl">
      <div className="flex flex-col gap-xl">
        <SectionHeader Icon={LinkIcon} title="Basic information" />
        <div className="flex flex-wrap gap-xl">
          <BrandField label="Brand name" value={name} />
          <BrandField label="Industry" value={basicInformation.industry} />
        </div>
        <div className="flex flex-wrap gap-xl">
          <BrandField label="Audience" value={basicInformation.audience} />
          <BrandField
            label="Brand website"
            value={
              basicInformation.website ? (
                <a
                  className="break-all text-utility-blue-700 underline"
                  href={basicInformation.website}
                  rel="noreferrer"
                  target="_blank"
                >
                  {basicInformation.website}
                </a>
              ) : null
            }
          />
        </div>
      </div>

      {socialLinks.length > 0 && (
        <>
          <div className="h-px w-full bg-border-primary" />
          <div className="flex flex-col gap-xl">
            <SectionHeader Icon={LinkIcon} title="Brand links" />
            <div className="flex flex-col gap-md">
              {socialLinks.map((link) => (
                <BrandLinkCard
                  key={link.id}
                  platform={link.platform}
                  url={link.url}
                />
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function BrandField({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex min-w-[120px] flex-1 flex-col gap-sm">
      <span className="text-sm font-medium text-text-primary">{label}</span>
      <div className="break-words text-base font-normal text-text-quaternary">
        {value}
      </div>
    </div>
  );
}

function BrandLinkCard({ platform, url }: { platform: string; url: string }) {
  return (
    <div className="flex items-center gap-md rounded-xl border border-border-secondary bg-bg-primary px-xl py-lg">
      <span className="min-w-0 flex-1 truncate text-sm font-medium text-text-brand-secondary">
        {formatBrandLink(platform, url)}
      </span>
      <button
        aria-label={`Open ${platform} link`}
        className="shrink-0 cursor-pointer rounded-md p-md text-text-brand-secondary hover:bg-bg-secondary"
        onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
        type="button"
      >
        <ExternalLinkIcon size={20} />
      </button>
    </div>
  );
}

function formatBrandLink(platform: string, url: string): string {
  const cleaned = url
    .replace(/^https?:\/\//, "")
    .replace(/^www\./, "")
    .replace(/\/+$/, "");

  if (cleaned.toLowerCase().includes(platform.toLowerCase())) return cleaned;

  return `${platform}/${cleaned}`;
}
